import React, { useState, useEffect, useRef } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { useQueryClient, useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { fetchClient, BACKEND_URL } from '@/lib/api';
import { checkAndHandle401 } from '@/lib/apiErrorHandler';
import type { SharePointSite, SharePointFolder, OneDriveDriveInfo, OneDriveFolder } from '@/lib/api';
import { Folder, ChevronRight, X, Globe, Loader2 } from 'lucide-react';
import FolderSearchInput from './FolderSearchInput';
import FolderSearchResults from './FolderSearchResults';
import type {
  SearchResultItem,
  SharePointSearchResponse,
} from '@/types/sharepoint-search';
import { SEARCH_MODE_FULL } from '@/types/sharepoint-search';

interface SharePointFolderModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onSelectFolder: (projectId: string, folderPath: string, folderId: string) => void;
}

interface PathSegment {
  id: string;
  name: string;
  webUrl?: string;
}

interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
  active?: boolean;
}

function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  if (items.length === 0) return null;
  return (
    <nav className="flex items-center gap-1 px-4 py-2 bg-muted/40 border-b border-border overflow-x-auto shrink-0">
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground/40 shrink-0" />}
          {item.active ? (
            <span
              className="text-[13px] font-medium text-foreground truncate max-w-[180px]"
              title={item.label}
            >
              {item.label}
            </span>
          ) : (
            <button
              type="button"
              onClick={item.onClick}
              className="text-[13px] text-muted-foreground hover:text-primary truncate max-w-[180px] transition-colors"
              title={item.label}
            >
              {item.label}
            </button>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

function BrowserRow({
  icon,
  name,
  subtitle,
  onClick,
}: {
  icon: React.ReactNode;
  name: string;
  subtitle?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/60 transition-colors group"
    >
      <span className="shrink-0 text-muted-foreground group-hover:text-primary transition-colors">
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-medium text-foreground truncate">{name}</span>
        {subtitle && (
          <span className="block text-xs text-muted-foreground truncate">{subtitle}</span>
        )}
      </span>
      <ChevronRight className="w-4 h-4 text-border group-hover:text-muted-foreground shrink-0 transition-colors" />
    </button>
  );
}

function BrowserEmpty({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
      <Folder className="w-10 h-10 mb-3 opacity-25" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

function BrowserLoading() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-5 h-5 animate-spin text-primary/60" />
    </div>
  );
}

/** Cache fetched data for 5 minutes to avoid redundant Graph calls on re-open. */
const GRAPH_CACHE_STALE_MS = 5 * 60 * 1000;

/**
 * Minimum query length before the search endpoint is hit. Mirrors
 * SEARCH_MIN_QUERY_LENGTH in FolderSearchInput; TanStack Query `enabled`
 * gate relies on this value independently.
 */
const SEARCH_ENABLE_MIN_LENGTH = 2;

/**
 * Cap on the number of OneDrive pages the UI will auto-fetch. Each page is
 * 200 items, so 10 pages = 2000 items. Beyond that the user is expected
 * to narrow with search rather than scrolling. Backend emits no truncated
 * flag by design; frontend owns the cap.
 */
const ONEDRIVE_MAX_PAGE_COUNT = 10;
const ONEDRIVE_ITEMS_PER_PAGE = 200;
const ONEDRIVE_TRUNCATED_ITEM_COUNT =
  ONEDRIVE_MAX_PAGE_COUNT * ONEDRIVE_ITEMS_PER_PAGE;

function normalizeOneDriveResponse(
  data: unknown,
): { folders: OneDriveFolder[]; next_link: string | null } {
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    const folders = Array.isArray(d.folders) ? (d.folders as OneDriveFolder[]) : [];
    const next = typeof d.next_link === 'string' ? d.next_link : null;
    return { folders, next_link: next };
  }
  return { folders: [], next_link: null };
}

export default function SharePointFolderModal({
  projectId,
  isOpen,
  onClose,
  onSelectFolder,
}: SharePointFolderModalProps) {
  const { backendToken } = useAuth();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('sharepoint');

  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null);
  const [selectedDrive, setSelectedDrive] = useState<PathSegment | null>(null);
  const [folders, setFolders] = useState<SharePointFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<SharePointFolder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<PathSegment[]>([]);

  const [odFolders, setOdFolders] = useState<OneDriveFolder[]>([]);
  const [odLoading, setOdLoading] = useState(false);
  const [odError, setOdError] = useState<string | null>(null);
  const [odDriveInfo, setOdDriveInfo] = useState<OneDriveDriveInfo | null>(null);
  const [odCurrentPath, setOdCurrentPath] = useState<PathSegment[]>([]);
  const [odLoaded, setOdLoaded] = useState(false);
  const [odNextLink, setOdNextLink] = useState<string | null>(null);
  const [odPagesLoaded, setOdPagesLoaded] = useState<number>(0);
  const odLoadingRef = useRef(false);

  useEffect(() => {
    if (isOpen && backendToken) {
      setSearchQuery('');
      setActiveTab('sharepoint');
      setSelectedSite(null);
      setSelectedDrive(null);
      setSelectedFolder(null);
      setFolders([]);
      setCurrentPath([]);
      setError(null);
      setOdFolders([]);
      setOdCurrentPath([]);
      setOdError(null);
      setOdDriveInfo(null);
      setOdLoaded(false);
      setOdNextLink(null);
      setOdPagesLoaded(0);
      odLoadingRef.current = false;
      loadSites();
    }
  }, [isOpen, backendToken]);

  // Uses `useQuery` directly because the POST /sharepoint/search endpoint
  // is not in the auto-generated openapi schema yet (manual type in
  // src/types/sharepoint-search.ts). When the schema is regenerated this
  // can be migrated to `$api.useQuery`.
  const searchEnabled =
    isOpen &&
    !!backendToken &&
    searchQuery.trim().length >= SEARCH_ENABLE_MIN_LENGTH;

  const {
    data: searchData,
    isLoading: searchLoading,
    isFetching: searchFetching,
  } = useQuery<SharePointSearchResponse>({
    queryKey: ['post', '/api/v1/sharepoint/search', searchQuery.trim()],
    enabled: searchEnabled,
    staleTime: GRAPH_CACHE_STALE_MS,
    // Keep previous results visible while the next key is loading so
    // typeahead doesn't flash "empty + spinner" on every keystroke.
    // Canonical TanStack v5 primitive; handles data/isPending/isLoading
    // transitions across queryKey changes more reliably than the
    // function form.
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      // Prefer the in-closure backendToken over reading localStorage:
      // the auth hook keeps this value in sync with the MSAL/test-auth
      // flow, and avoids a stale-token read when another tab mutates
      // localStorage mid-session.
      const token = backendToken || '';
      // `signal` aborts automatically when TanStack supersedes this
      // query, which cancels the in-flight fetch for stale keystrokes.
      const res = await fetch(`${BACKEND_URL}/api/v1/sharepoint/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ q: searchQuery.trim() }),
        signal,
      });
      // Hand 401s to the shared handler so the global logout/refresh
      // middleware runs -- raw fetch would otherwise swallow them and
      // the user would see "No matches" instead of being redirected to
      // the login page.
      checkAndHandle401(res);
      if (!res.ok) throw new Error('search_failed');
      return (await res.json()) as SharePointSearchResponse;
    },
  });

  const loadSites = async () => {
    if (!backendToken) return;
    setLoading(true);
    setError(null);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["get", "/api/v1/sharepoint/sites"],
        queryFn: async () => {
          const { data, error } = await fetchClient.GET("/api/v1/sharepoint/sites");
          if (error) throw new Error('Failed to load SharePoint sites');
          return Array.isArray(data) ? data as SharePointSite[] : [];
        },
        staleTime: GRAPH_CACHE_STALE_MS,
      });
      setSites(data);
    } catch {
      setError('Failed to load SharePoint sites. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async (driveId: string, folderId?: string | null) => {
    if (!backendToken) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await fetchClient.GET("/api/v1/sharepoint/folders", {
        params: { query: { siteId: driveId, folderId: folderId || undefined } },
      });
      if (fetchError) throw new Error('Failed to load folders');
      setFolders(Array.isArray(data) ? data : []);
    } catch {
      setError('Failed to load folders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSiteSelect = (site: SharePointSite) => {
    if (!site.id) return;
    setSelectedSite(site);
    // Auto-select the default drive and load folders immediately,
    // skipping the intermediate drive selection screen.
    setSelectedDrive({ id: site.id, name: 'Documents', webUrl: site.webUrl ?? undefined });
    setSelectedFolder(null);
    setCurrentPath([]);
    setFolders([]);
    loadFolders(site.id);
  };

  const handleFolderSelect = (folder: SharePointFolder) => {
    if (!selectedDrive) return;
    setSelectedFolder(folder);
    setCurrentPath(prev => [...prev, { id: folder.id ?? '', name: folder.name ?? '', webUrl: folder.webUrl ?? undefined }]);
    loadFolders(selectedDrive.id, folder.id);
  };

  const spResetToSites = () => {
    setSelectedSite(null);
    setSelectedDrive(null);
    setSelectedFolder(null);
    setFolders([]);
    setCurrentPath([]);
  };

  const spResetToDriveRoot = () => {
    if (!selectedDrive) return;
    setCurrentPath([]);
    setSelectedFolder(null);
    loadFolders(selectedDrive.id);
  };

  const spNavigateToPathIndex = (index: number) => {
    if (!selectedDrive) return;
    const newPath = currentPath.slice(0, index + 1);
    setCurrentPath(newPath);
    const target = newPath[newPath.length - 1];
    setSelectedFolder({ id: target.id, name: target.name, webUrl: target.webUrl });
    loadFolders(selectedDrive.id, target.id);
  };

  const loadOneDriveRoot = async () => {
    if (!backendToken || odLoaded || odLoadingRef.current) return;
    odLoadingRef.current = true;
    setOdLoading(true);
    setOdError(null);
    try {
      // Sequential fetch -- both share the same auth token; avoids concurrent
      // token refresh race when the token is near expiry.
      const info = await queryClient.fetchQuery({
        queryKey: ["get", "/api/v1/onedrive/drive-info"],
        queryFn: async () => {
          const { data, error } = await fetchClient.GET("/api/v1/onedrive/drive-info");
          if (error) throw new Error('Failed to load OneDrive info');
          return (data ?? null) as OneDriveDriveInfo | null;
        },
        staleTime: GRAPH_CACHE_STALE_MS,
      });
      setOdDriveInfo(info);

      const page = await queryClient.fetchQuery({
        queryKey: ["get", "/api/v1/onedrive/folders"],
        queryFn: async () => {
          const { data, error } = await fetchClient.GET("/api/v1/onedrive/folders");
          if (error) throw new Error('Failed to load OneDrive folders');
          return normalizeOneDriveResponse(data);
        },
        staleTime: GRAPH_CACHE_STALE_MS,
      });
      setOdFolders(page.folders);
      setOdNextLink(page.next_link);
      setOdPagesLoaded(1);
      setOdLoaded(true);
    } catch {
      setOdError('Failed to load OneDrive folders. Please try again.');
    } finally {
      odLoadingRef.current = false;
      setOdLoading(false);
    }
  };

  const loadOdSubfolders = async (folderId?: string) => {
    setOdLoading(true);
    setOdError(null);
    try {
      const { data, error: fetchError } = await fetchClient.GET("/api/v1/onedrive/folders", {
        params: { query: { folder_id: folderId || undefined } },
      });
      if (fetchError) throw new Error('Failed to load OneDrive folders');
      const page = normalizeOneDriveResponse(data);
      setOdFolders(page.folders);
      setOdNextLink(page.next_link);
      setOdPagesLoaded(1);
    } catch {
      setOdError('Failed to load folders. Please try again.');
    } finally {
      setOdLoading(false);
    }
  };

  const loadOdNextPage = async () => {
    if (!odNextLink) return;
    if (odPagesLoaded >= ONEDRIVE_MAX_PAGE_COUNT) return;
    setOdLoading(true);
    setOdError(null);
    try {
      // Forward next_link as a query parameter. Backend validates SSRF
      // prefix before forwarding to Graph.
      const token = backendToken || '';
      const url = `${BACKEND_URL}/api/v1/onedrive/folders?next_link=${encodeURIComponent(odNextLink)}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Route 401s through the shared handler (see search queryFn rationale).
      checkAndHandle401(res);
      if (!res.ok) throw new Error('next_page_failed');
      const body = await res.json();
      const page = normalizeOneDriveResponse(body);
      setOdFolders((prev) => [...prev, ...page.folders]);
      setOdNextLink(page.next_link);
      setOdPagesLoaded((prev) => prev + 1);
    } catch {
      setOdError('Failed to load more folders. Please try again.');
    } finally {
      setOdLoading(false);
    }
  };

  const handleOdFolderClick = (folder: OneDriveFolder) => {
    setOdCurrentPath(prev => [...prev, { id: folder.id ?? '', name: folder.name ?? '', webUrl: folder.web_url ?? undefined }]);
    loadOdSubfolders(folder.id ?? undefined);
  };

  const handleOdNavigateToPath = (index: number) => {
    if (index === -1) {
      setOdCurrentPath([]);
      loadOdSubfolders();
    } else {
      const newPath = odCurrentPath.slice(0, index + 1);
      setOdCurrentPath(newPath);
      loadOdSubfolders(newPath[newPath.length - 1].id);
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === 'onedrive') {
      loadOneDriveRoot();
    }
  };

  const handleSearchSelectFolder = (item: SearchResultItem) => {
    // `web_url` is the stable path the backend persists; `id` is the Graph ID.
    onSelectFolder(projectId, item.web_url ?? item.name, item.id ?? '');
    onClose();
  };

  const handleSearchDrillIntoSite = (item: SearchResultItem) => {
    setSearchQuery('');
    setActiveTab('sharepoint');
    if (!item.id) return;
    handleSiteSelect({
      id: item.id,
      name: item.name,
      displayName: item.name,
      webUrl: item.web_url ?? undefined,
    } as SharePointSite);
  };

  const handleConfirmSharePoint = () => {
    if (!selectedSite) return;
    if (currentPath.length > 0) {
      const finalFolder = currentPath[currentPath.length - 1];
      onSelectFolder(projectId, finalFolder.webUrl || finalFolder.name, finalFolder.id);
    } else if (selectedFolder) {
      onSelectFolder(projectId, selectedFolder.webUrl ?? selectedFolder.name ?? '', selectedFolder.id ?? '');
    } else if (selectedDrive) {
      onSelectFolder(projectId, selectedDrive.webUrl || selectedSite.webUrl || selectedSite.name || '', selectedDrive.id || selectedSite.id || '');
    } else {
      onSelectFolder(projectId, selectedSite.webUrl || selectedSite.name || '', selectedSite.id || '');
    }
    onClose();
  };

  const handleConfirmOneDrive = () => {
    if (odCurrentPath.length > 0) {
      const finalFolder = odCurrentPath[odCurrentPath.length - 1];
      onSelectFolder(projectId, finalFolder.webUrl || finalFolder.name, finalFolder.id);
    } else if (odDriveInfo?.web_url) {
      onSelectFolder(projectId, odDriveInfo.web_url, odDriveInfo.id || 'root');
    }
    onClose();
  };

  if (!isOpen) return null;

  const canConfirmSP = !!selectedSite;
  const canConfirmOD = odCurrentPath.length > 0 || !!odDriveInfo?.web_url;
  const isSearching = searchQuery.trim().length >= SEARCH_ENABLE_MIN_LENGTH;

  const spBreadcrumbs: BreadcrumbItem[] = [];
  if (selectedSite) {
    spBreadcrumbs.push({ label: 'Sites', onClick: spResetToSites });
    spBreadcrumbs.push({
      label: selectedSite.name ?? '',
      onClick: currentPath.length > 0 ? spResetToDriveRoot : undefined,
      active: currentPath.length === 0,
    });
    currentPath.forEach((seg, i) => {
      const isLast = i === currentPath.length - 1;
      spBreadcrumbs.push({
        label: seg.name,
        onClick: isLast ? undefined : () => spNavigateToPathIndex(i),
        active: isLast,
      });
    });
  }

  const odBreadcrumbs: BreadcrumbItem[] = [];
  if (odCurrentPath.length > 0) {
    odBreadcrumbs.push({
      label: 'My OneDrive',
      onClick: () => handleOdNavigateToPath(-1),
    });
  }
  odCurrentPath.forEach((seg, i) => {
    const isLast = i === odCurrentPath.length - 1;
    odBreadcrumbs.push({
      label: seg.name,
      onClick: isLast ? undefined : () => handleOdNavigateToPath(i),
      active: isLast,
    });
  });

  const spButtonLabel = currentPath.length > 0
    ? 'Connect Folder'
    : selectedDrive
      ? 'Connect Library'
      : 'Connect Site';

  const odButtonLabel = odCurrentPath.length > 0 ? 'Connect Folder' : 'Connect';

  const odHasMore = odNextLink !== null;
  const odReachedCap =
    odPagesLoaded >= ONEDRIVE_MAX_PAGE_COUNT && odNextLink !== null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        className="bg-background rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col overflow-hidden border border-border"
        style={{ maxHeight: 'min(640px, 85vh)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h2 className="text-base font-semibold text-foreground">Select Folder</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search input (always visible above the tabs / results) */}
        <FolderSearchInput
          onChange={(q) => setSearchQuery(q)}
          autoFocus
        />

        {/* Error banner */}
        {(error || odError) && (
          <div className="mx-5 mt-3 px-3 py-2 bg-destructive/5 border border-destructive/20 rounded-lg text-sm text-destructive shrink-0">
            {error || odError}
          </div>
        )}

        {isSearching ? (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Inline activity indicator: shown on refetch when prior
                results are still visible. Signals "refreshing" without
                hiding results or triggering the first-load spinner. */}
            {searchFetching && (searchData?.results.length ?? 0) > 0 && (
              <div
                className="flex items-center gap-2 px-5 py-1.5 text-[11px] text-muted-foreground border-b border-border/50 bg-muted/20 shrink-0"
                data-testid="search-refetching-indicator"
              >
                <Loader2 className="w-[14px] h-[14px] animate-spin text-primary/60" />
                <span>Refreshing...</span>
              </div>
            )}
            <div className="flex-1 overflow-y-auto" data-testid="search-panel">
              <FolderSearchResults
                results={searchData?.results ?? []}
                searchMode={searchData?.search_mode ?? SEARCH_MODE_FULL}
                isLoading={searchLoading}
                onSelectFolder={handleSearchSelectFolder}
                onDrillIntoSite={handleSearchDrillIntoSite}
              />
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-muted/30 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-1.5 text-[13px] font-medium border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <Tabs.Root
            value={activeTab}
            onValueChange={handleTabChange}
            className="flex-1 flex flex-col min-h-0"
          >
            {/* Tab triggers */}
            <Tabs.List className="flex border-b border-border px-5 shrink-0">
              <Tabs.Trigger
                value="sharepoint"
                className="px-4 py-2.5 text-[13px] font-medium text-muted-foreground border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary transition-colors"
              >
                SharePoint Sites
              </Tabs.Trigger>
              <Tabs.Trigger
                value="onedrive"
                className="px-4 py-2.5 text-[13px] font-medium text-muted-foreground border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary transition-colors"
              >
                My OneDrive
              </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="sharepoint" className="flex-1 flex flex-col min-h-0">
              <Breadcrumbs items={spBreadcrumbs} />

              <div className="flex-1 overflow-y-auto">
                {/* Sites list (initial view) */}
                {!selectedSite && (
                  <>
                    {loading ? (
                      <BrowserLoading />
                    ) : sites.length === 0 ? (
                      <BrowserEmpty message="No SharePoint sites found" />
                    ) : (
                      <div className="divide-y divide-border/50">
                        {sites.map((site) => (
                          <BrowserRow
                            key={site.id ?? ''}
                            icon={<Globe className="w-[18px] h-[18px]" />}
                            name={site.name ?? site.displayName ?? ''}
                            subtitle={site.webUrl ?? undefined}
                            onClick={() => handleSiteSelect(site)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Folder browser (after site selected) */}
                {selectedSite && selectedDrive && (
                  <>
                    {loading ? (
                      <BrowserLoading />
                    ) : folders.length === 0 ? (
                      <BrowserEmpty message="No subfolders found" />
                    ) : (
                      <div className="divide-y divide-border/50">
                        {folders.map((folder) => (
                          <BrowserRow
                            key={folder.id ?? ''}
                            icon={<Folder className="w-[18px] h-[18px]" />}
                            name={folder.name ?? ''}
                            subtitle={folder.path ?? undefined}
                            onClick={() => handleFolderSelect(folder)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-muted/30 shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3.5 py-1.5 text-[13px] font-medium border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSharePoint}
                  disabled={!canConfirmSP}
                  className="px-3.5 py-1.5 text-[13px] font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {spButtonLabel}
                </button>
              </div>
            </Tabs.Content>

            <Tabs.Content value="onedrive" className="flex-1 flex flex-col min-h-0">
              <Breadcrumbs items={odBreadcrumbs} />

              <div className="flex-1 overflow-y-auto">
                {(odLoading && odFolders.length === 0) || !odLoaded ? (
                  <BrowserLoading />
                ) : odFolders.length === 0 ? (
                  <BrowserEmpty message="No folders found" />
                ) : (
                  <>
                    <div className="divide-y divide-border/50">
                      {odFolders.map((folder, idx) => (
                        <BrowserRow
                          key={`${folder.id ?? ''}-${idx}`}
                          icon={<Folder className="w-[18px] h-[18px]" />}
                          name={folder.name ?? ''}
                          onClick={() => handleOdFolderClick(folder)}
                        />
                      ))}
                    </div>
                    {odReachedCap && (
                      <div
                        className="px-4 py-3 bg-amber-50 border-t border-amber-200 text-[12px] text-amber-900"
                        data-testid="onedrive-truncated-notice"
                      >
                        Truncated - showing first {ONEDRIVE_TRUNCATED_ITEM_COUNT} items. Use search to find specific folders.
                      </div>
                    )}
                    {odHasMore && !odReachedCap && (
                      <div className="px-4 py-3 border-t border-border bg-muted/20 flex items-center justify-center">
                        <button
                          type="button"
                          onClick={loadOdNextPage}
                          disabled={odLoading}
                          data-testid="onedrive-load-more"
                          className="px-3.5 py-1.5 text-[13px] font-medium border border-border rounded-lg text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {odLoading ? 'Loading...' : 'Load more'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-muted/30 shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3.5 py-1.5 text-[13px] font-medium border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmOneDrive}
                  disabled={!canConfirmOD}
                  className="px-3.5 py-1.5 text-[13px] font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {odButtonLabel}
                </button>
              </div>
            </Tabs.Content>
          </Tabs.Root>
        )}
      </div>
    </div>
  );
}
